import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';
import {map, switchMap, catchError} from "rxjs/operators";
import {of, timer} from "rxjs";
import {AccountControllerService} from "@aparraga/ahoracrypto-api-sdk/sdk/api/accountController.service";
import {WalletControllerService} from "@aparraga/ahoracrypto-api-sdk/sdk/api/walletController.service";
import {CountryService} from "../country/country.service";
import {AccountService} from "../account/account.service";
import {Injectable, Injector} from '@angular/core';
import {RegistrationFlowHelperService} from "../flowhelpers/registrationflowhelper.service";
import {CryptoService} from "../crypto/crypto.service";
import Card from "creditcards/card";
import types from "creditcards-types";
import {cvc, expiration} from "creditcards";
import * as IBAN from 'iban-ts';
import { PhoneNumberUtil, PhoneNumber } from 'google-libphonenumber';

@Injectable({
  providedIn: 'root'
})
export class CustomValidators {

  public constructor(protected accountControllerService: AccountControllerService,
                     protected walletControllerService: WalletControllerService,
                     protected accountService: AccountService,
                     private injector: Injector,
                     protected countryService: CountryService,
                     protected cryptoService: CryptoService) {
  }

  public matchValidator(source: string, target: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const sourceCtrl = control.get(source);
      const targetCtrl = control.get(target);

      return sourceCtrl && targetCtrl && sourceCtrl.value !== targetCtrl.value
        ? { mismatch: true }
        : null;
    };
  }

  public validateEmailVerificationCode(time: number = 500) {

    return (control: AbstractControl) => {

      const regex = /[^0-9]+/gi;

      let emailVerificationCode = control.value?.replace(regex, '') || '';

      if(emailVerificationCode != null && emailVerificationCode.length == 6) {

        let emailInfo = {
          temporalUserId: this.injector.get(RegistrationFlowHelperService).temporalUserId,
          code: emailVerificationCode
        };

        return timer(time).pipe(
          switchMap(() => this.accountControllerService.accountControllerVerifyEmail(emailInfo).pipe(
            map(response => {
              if(response != null) {
                this.accountService.setJwt(response);
                return null;
              } else {
                return {invalid: true};
              }
            }),
            catchError(error => {
              console.error('Error validating email code:', error);
              return of({invalid: true});
            })
          ))
        );

      } else {
        return of(null);
      }
    };
  }

  public validatePhoneNumber(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      
      try {
        const phoneUtil = PhoneNumberUtil.getInstance();
        const form = control.parent;
        
        if (!form) return null;
        
        const prefixControl = form.get('phonePrefix');
        if (!prefixControl || !prefixControl.value) return null;
        
        // combine prefix with number, remove all non-numeric characters
        const fullNumber = `${prefixControl.value}${control.value.replace(/[^0-9]/g, '')}`;
        //if fullNumber is empty, return null
        if(fullNumber.length == 0) return null;
        
        try {
          const phoneNumber = phoneUtil.parse(fullNumber);
          
          if (!phoneUtil.isValidNumber(phoneNumber)) {
            return { invalidPhone: { message: 'Invalid phone number' } };
          }
          
          return null;
        } catch (e) {
          return { invalidPhone: { message: 'Invalid phone number format' } };
        }
      } catch (e) {
        return { invalidPhone: { message: 'Invalid phone number format' } };
      }
    };
  }

  public validatePhoneVerificationCode(time: number = 500) {

    return (control: AbstractControl) => {

      const regex = /[^0-9]+/gi;

      let phoneVerificationCode = control.value?.replace(regex, '') || '';

      if(phoneVerificationCode != null && phoneVerificationCode.length == 6) {

        let phoneInfo = {
          temporalUserId: this.injector.get(RegistrationFlowHelperService).temporalUserId,
          code: phoneVerificationCode
        };

        return timer(time).pipe(
          switchMap(() => this.accountControllerService.accountControllerVerifyPhone(phoneInfo).pipe(
            map(response => {
              if(response != null) {
                this.accountService.setJwt(response);
                return null;
              } else {
                return {invalid: true};
              }
            }),
            catchError(error => {
              console.error('Error validating phone code:', error);
              return of({invalid: true});
            })
          ))
        );
      } else {
        return of(null);
      }
    };
  }

  public validateBaseCurrencyAmount(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if(control != null && control.value != null) {
        const baseCurrencyAmount = control.value.trim();
        const profileLevel = this.accountService.getUserInSession() != null && this.accountService.getUserInSession().profileLevel != null?this.accountService.getUserInSession().profileLevel:0;
        if (profileLevel >= 5 ||
           (baseCurrencyAmount != null && baseCurrencyAmount && !isNaN(baseCurrencyAmount) && baseCurrencyAmount >= 50 && baseCurrencyAmount <= 50000)) {
          return null;
        } else {
          return {invalidFormat: {message: "Please indicate a value between 50 and 50000"}};
        }
      }
      return null;
    }
  }

  public validateIban(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if(control != null && control.value != null) {
        const iban = control.value.trim();
        if (iban != null && iban.length > 0) {
          const valid = IBAN.isValid(iban);
          if (valid) {
            return null;
          }
        }
        return {invalidFormat: {message: "Please indicate a valid IBAN"}};
      }
      return null;
    }
  }

  public validateWalletAddress(time: number = 500) {

    return (control: AbstractControl) => {

      let address = control.value?.trim() || '';

      if (address != null && address.length > 0) {
        let selectedCrypto = this.cryptoService.getSelectedCrypto();
        let selectedNetwork = this.cryptoService.getSelectedNetwork();
        if (selectedCrypto != null && selectedNetwork != null) {
          let symbol = selectedCrypto.baseCurrency;
          let network = selectedNetwork.network;
          return timer(time).pipe(
            switchMap(() => this.walletControllerService.walletControllerValidateWalletAddress(symbol, address, network).pipe(
              map(response => {
                if (!response) {
                  return {invalid: true};
                } else {
                  return null;
                }
              }),
              catchError(error => {
                console.error('Error validating wallet address:', error);
                return of({invalid: true});
              })
            ))
          );
        } else {
          return of(null);
        }
      }
      else {
        return of(null);
      }

    }

  }

  public validateWalletMemo(time: number = 500) {

    return (control: AbstractControl) => {

      let memo = control.value != null ? control.value.trim() : '';

      if (memo != null && memo.length > 0) {
        let selectedCrypto = this.cryptoService.getSelectedCrypto();
        let selectedNetwork = this.cryptoService.getSelectedNetwork();
        if (selectedCrypto != null && selectedNetwork != null) {
          let memoRegex = selectedNetwork.memoRegex;
          if (memoRegex != null && memoRegex.length > 0) {
            let regex = new RegExp(memoRegex);
            if (!regex.test(memo)) {
              return of({invalid: true});
            }
          }
          return of(null);
        } else {
          return of(null);
        }
      }
      else {
        return of(null);
      }

    }

  }

  public validateCreditCardNumber() {

    return (control: AbstractControl) => {
      if(control != null && control.value != null) {
        let cardNumber = control.value.replace(/[^\d]/g, '');
        let type = types.find(type => type.test(cardNumber, true));

        if(type != null) {
          let card = Card([type]);

          if (card.isValid(cardNumber)) {
            return null;
          } else {
            return {invalid: true}
          }
        }
        else {
          return {invalid: true}
        }
      }
      else {
        return null;
      }
    }


  }

  public validateCreditCardExpirationDate() {
    return (control: AbstractControl) => {
      if(control != null && control.value != null) {
        let date = control.value.trim();

        //parse date as MM/YY
        let dateArray = date.split("/");
        let month = expiration.month.parse(dateArray[0]);
        let year = expiration.year.parse(dateArray[1], true);

        if (expiration.month.isValid(month) && expiration.year.isValid(year)) {
          if (!expiration.isPast(month, year)) {
            return null;
          } else {
            return {invalid: true, message: "Expiration date is in the past"}
          }
        } else {
          return {invalid: true, message: "Invalid date format"}
        }
      }
      else {
        return null;
      }
    }
  }

  public validateCVC() {
    return (control: AbstractControl) => {
      if(control != null && control.value != null) {
        let cvcValue = control.value.replace(/[^\d]/g, '');

        if (cvc.isValid(cvcValue)) {
          return null;
        } else {
          return {invalid: true}
        }
      }
      else {
        return null;
      }
    }
  }

}
